import {
  AUTHORED_INTERIORS,
  INTERIOR_STABILIZATION_CONFIG,
  type AuthoredInteriorDefinition,
  type AuthoredInteriorId,
  type InteriorLocalVolume,
} from "../../config/interiors.ts";
import type { InteriorPresenceState } from "./types.ts";

export function createInteriorPresenceState(): InteriorPresenceState {
  return {
    activeInteriorId: null,
    candidateInteriorId: null,
    candidateTicks: 0,
    outsideTicks: 0,
    entryCount: 0,
    exitCount: 0,
  };
}

function worldToInteriorLocal(
  interior: Readonly<AuthoredInteriorDefinition>,
  position: Readonly<{ x: number; y: number; z: number }>,
): Readonly<{ x: number; y: number; z: number }> {
  const deltaX = position.x - interior.origin.x;
  const deltaZ = position.z - interior.origin.z;
  const cosine = Math.cos(interior.rotationYRadians);
  const sine = Math.sin(interior.rotationYRadians);
  return {
    x: deltaX * cosine - deltaZ * sine,
    y: position.y - interior.origin.y,
    z: deltaX * sine + deltaZ * cosine,
  };
}

function volumeContains(
  volume: Readonly<InteriorLocalVolume>,
  local: Readonly<{ x: number; y: number; z: number }>,
  paddingM: number,
): boolean {
  return (
    Math.abs(local.x - volume.centerLocalM.x) <=
      Math.max(0, volume.halfSizeM.x + paddingM) &&
    Math.abs(local.y - volume.centerLocalM.y) <=
      // Presence is sampled at the grounded foot position. Entry inset is a
      // horizontal boundary stabilizer; shrinking the vertical volume would
      // exclude a player standing exactly on an authored floor.
      Math.max(0, volume.halfSizeM.y + Math.max(0, paddingM)) &&
    Math.abs(local.z - volume.centerLocalM.z) <=
      Math.max(0, volume.halfSizeM.z + paddingM)
  );
}

export function interiorContainsWorldPosition(
  interior: Readonly<AuthoredInteriorDefinition>,
  position: Readonly<{ x: number; y: number; z: number }>,
  paddingM = 0,
): boolean {
  const local = worldToInteriorLocal(interior, position);
  return interior.volumes.some((volume) =>
    volumeContains(volume, local, paddingM)
  );
}

function interiorAt(
  position: Readonly<{ x: number; y: number; z: number }>,
): AuthoredInteriorId | null {
  return AUTHORED_INTERIORS.find((interior) =>
    interiorContainsWorldPosition(
      interior,
      position,
      -INTERIOR_STABILIZATION_CONFIG.entryInsetM,
    )
  )?.id ?? null;
}

export function stepInteriorPresence(
  state: Readonly<InteriorPresenceState>,
  position: Readonly<{ x: number; y: number; z: number }>,
  enabled: boolean,
): InteriorPresenceState {
  if (!enabled) {
    return state.activeInteriorId === null &&
        state.candidateInteriorId === null &&
        state.outsideTicks === 0
      ? state
      : {
          ...state,
          activeInteriorId: null,
          candidateInteriorId: null,
          candidateTicks: 0,
          outsideTicks: 0,
          exitCount: state.exitCount + (state.activeInteriorId === null ? 0 : 1),
        };
  }

  if (state.activeInteriorId !== null) {
    const active = AUTHORED_INTERIORS.find(
      (interior) => interior.id === state.activeInteriorId,
    );
    const stillInside = active !== undefined && interiorContainsWorldPosition(
      active,
      position,
      INTERIOR_STABILIZATION_CONFIG.exitPaddingM,
    );
    if (stillInside) {
      return state.outsideTicks === 0
        ? state
        : { ...state, outsideTicks: 0 };
    }
    const outsideTicks = state.outsideTicks + 1;
    if (outsideTicks < INTERIOR_STABILIZATION_CONFIG.exitConfirmationTicks) {
      return { ...state, outsideTicks };
    }
    return {
      ...state,
      activeInteriorId: null,
      candidateInteriorId: null,
      candidateTicks: 0,
      outsideTicks: 0,
      exitCount: state.exitCount + 1,
    };
  }

  const candidateInteriorId = interiorAt(position);
  if (candidateInteriorId === null) {
    return state.candidateInteriorId === null && state.candidateTicks === 0
      ? state
      : {
          ...state,
          candidateInteriorId: null,
          candidateTicks: 0,
        };
  }
  const candidateTicks = state.candidateInteriorId === candidateInteriorId
    ? state.candidateTicks + 1
    : 1;
  if (candidateTicks < INTERIOR_STABILIZATION_CONFIG.entryConfirmationTicks) {
    return {
      ...state,
      candidateInteriorId,
      candidateTicks,
    };
  }
  return {
    ...state,
    activeInteriorId: candidateInteriorId,
    candidateInteriorId: null,
    candidateTicks: 0,
    outsideTicks: 0,
    entryCount: state.entryCount + 1,
  };
}
